import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that keeps the fixed discount tier shown for a linked
 * product in sync with the main product's live quantity selector.
 */
class QuantityFixedOfferComponent extends Component {
  #abortController = new AbortController();
  #wasChecked = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }

    this.addEventListener('pointerdown', this.#onOptionPointerDown, { signal });
    this.addEventListener('click', this.#onRadioClick, { signal });

    this.#disableProductLinks();
    this.#syncFromInput();
  }

  /**
   * The linked product is rendered through the shared product-card-small snippet, which always
   * includes a link to the product page. This offer only supports adding the product to the
   * cart, so the link is neutralized: `data-no-navigation` stops the product-card component's
   * own click-to-navigate behavior, and removing `href` stops the native anchor navigation
   * (including keyboard activation, which `pointer-events: none` alone would not cover).
   */
  #disableProductLinks() {
    for (const link of this.querySelectorAll('.product-card__link')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      link.removeAttribute('href');
      link.setAttribute('tabindex', '-1');
    }

    for (const card of this.querySelectorAll('product-card')) {
      card.setAttribute('data-no-navigation', 'true');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getProductForm() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const { productId } = this.dataset;

    if (productId) {
      return scope.querySelector(`product-form-component[data-product-id="${productId}"]`);
    }

    return scope.querySelector('product-form-component');
  }

  #getQuantityValue() {
    const productForm = this.#getProductForm();
    const quantitySelector = productForm?.querySelector('quantity-selector-component');
    if (!quantitySelector || typeof quantitySelector.getValue !== 'function') return null;

    const value = Number.parseInt(quantitySelector.getValue(), 10);
    return Number.isNaN(value) ? null : value;
  }

  /**
   * Resolves which pre-rendered price tier should be shown for a given quantity: the highest
   * configured tier quantity that does not exceed it (so a merchant only needs to add a tier at
   * each breakpoint, not one per quantity — e.g. a single "4+" tier covers 4, 5, 6, 7 until an
   * "8+" tier takes over). Falls back to the no-discount "default" tier below the lowest one.
   * @param {number} quantity
   * @param {HTMLElement} group
   * @returns {string}
   */
  #getTierKey(quantity, group) {
    const tierQuantities = Array.from(group.querySelectorAll('[data-price-tier]'))
      .map((tier) => (tier instanceof HTMLElement ? tier.dataset.priceTier : null))
      .filter((key) => key !== null && key !== 'default')
      .map((key) => Number.parseInt(/** @type {string} */ (key), 10))
      .filter((tierQty) => !Number.isNaN(tierQty))
      .sort((a, b) => a - b);

    let matched = null;
    for (const tierQty of tierQuantities) {
      if (tierQty > quantity) break;
      matched = tierQty;
    }

    return matched === null ? 'default' : matched.toString();
  }

  /**
   * @param {number} quantity
   */
  #setActiveTier(quantity) {
    const tierGroups = this.querySelectorAll('[data-price-tiers]');

    for (const group of tierGroups) {
      if (!(group instanceof HTMLElement)) continue;
      const tierKey = this.#getTierKey(quantity, group);
      const tiers = group.querySelectorAll('[data-price-tier]');
      for (const tier of tiers) {
        if (!(tier instanceof HTMLElement)) continue;
        tier.hidden = tier.dataset.priceTier !== tierKey;
      }
    }
  }

  #syncFromInput() {
    const quantity = this.#getQuantityValue();
    if (quantity === null) return;
    this.#setActiveTier(quantity);
  }

  /**
   * Records whether the option about to be interacted with is already selected, so the
   * following click can be recognized as a toggle-off rather than a normal selection. Since
   * the opt-out option is visually hidden, the linked product's own label/card is the only
   * visible control and needs to behave like a toggle: tapping it again deselects it.
   * @param {PointerEvent} event
   */
  #onOptionPointerDown = (event) => {
    const label =
      event.target instanceof Element ? event.target.closest('.quantity-fixed-offer__option') : null;
    const input = label?.querySelector('input.quantity-fixed-offer__radio[type="radio"]');
    this.#wasChecked = input instanceof HTMLInputElement ? input.checked : false;
  };

  /**
   * @param {MouseEvent} event
   */
  #onRadioClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || target.value === '') return;
    if (!this.#wasChecked) return;

    const optOutInput = this.querySelector('input.quantity-fixed-offer__radio[type="radio"][value=""]');
    if (!(optOutInput instanceof HTMLInputElement) || optOutInput.checked) return;

    optOutInput.checked = true;
    optOutInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onQuantityUpdate = (event) => {
    if (!(event.detail?.quantity >= 0)) return;

    const productForm = this.#getProductForm();
    if (productForm && event.target instanceof Node && !productForm.contains(event.target)) return;

    this.#setActiveTier(event.detail.quantity);
  };

  #onVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct?.id) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newHtml = event.detail?.data?.html;
    if (!newHtml || !this.dataset.blockId) return;

    const newComponent = newHtml.querySelector(
      `quantity-fixed-offer-component[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newComponent) return;

    this.innerHTML = newComponent.innerHTML;
    if (newComponent.dataset.productId) {
      this.dataset.productId = newComponent.dataset.productId;
    }

    this.#disableProductLinks();

    this.#syncFromInput();
  };
}

if (!customElements.get('quantity-fixed-offer-component')) {
  customElements.define('quantity-fixed-offer-component', QuantityFixedOfferComponent);
}
